import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Anchor,
  Badge,
  Button,
  Divider,
  Image,
  Loader,
  Skeleton,
  TextInput
} from '@mantine/core';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Plus, Trash } from 'tabler-icons-react';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import PageView from '../../common/PageView';
import PageCard from '../../common/PageCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import {
  useGlobalFormState,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import AppCard from '../../common/AppCard';
import StoreProductAssignModal from './StoreProductAssignModal';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import ActionableIcon from '../../common/ActionableIcon';
import { getEcomStoreProductStatus } from '../../../helpers/vendorHelper';
import CustomNumberInput from '../../common/CustomNumberInput';
import { triggerNotification } from '../../../helpers/notification';
import ConfirmModal from '../../common/ConfirmModal';
import { useStoreBackPath } from '../../../helpers/storeHelper';

const MENU_ACTIONS = { ADD_PRODUCT: 'ADD_PRODUCT', DELETE: 'delete' };

const StoreTaxEditView = ({ loading, ecomStore, duplicate }) => {
  const navigate = useNavigate();
  const { pkEcomStoreTax } = useParams();
  const { state: authState } = useContext(AuthContext);
  const { state, createStoreTax, updateStoreTax, deleteStoreTax } = useContext(
    StoreContext
  );
  const backPath = useStoreBackPath('/merchant/settings/taxes');
  const { isTabletOrSmaller, isLargeMobileOrSmaller } = useMediaQueryIndex();
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(
    {
      name: '',
      taxRate: null,
      pkEcomStoreProducts: []
    },
    { confirmDiscard: true, containerWidth: 900 }
  );
  const viewLoading =
    !ecomStore || (pkEcomStoreTax && (!hasInitialized || loading));

  const ecomStoreTax = state.ecomStoreTax.value.find(
    (t) => t.pkEcomStoreTax.toString() === pkEcomStoreTax
  );
  const selectedProducts = state.ecomStoreProducts.value.filter((p) =>
    formState.pkEcomStoreProducts?.includes(p.pkEcomStoreProduct)
  );

  useEffect(() => {
    if (!pkEcomStoreTax || ecomStoreTax) {
      resetFormState({
        name: ecomStoreTax?.name ?? '',
        taxRate: ecomStoreTax?.taxRate ?? null,
        pkEcomStoreProducts:
          ecomStoreTax?.ecomStoreProductTax.map((t) => t.fkEcomStoreProduct) ||
          []
      });
    }
  }, [ecomStoreTax, state.ecomStoreProducts.loading, duplicate]);

  const onSaveTax = () => {
    submitFormState((formData, onErrorCallback) => {
      if (ecomStoreTax && !duplicate) {
        updateStoreTax(
          ecomStoreTax.fkEcomStore,
          {
            ecomStoreTax: [
              {
                ...formState,
                pkEcomStoreTax: ecomStoreTax.pkEcomStoreTax
              }
            ]
          },
          (data) => {
            navigate(`/merchant/settings/taxes/${data[0]?.pkEcomStoreTax}`);
            triggerNotification('Tax updated!', 'success');
          },
          onErrorCallback
        );
      }
      else {
        createStoreTax(
          authState.pkEcomStore,
          { ecomStoreTax: [formState] },
          (data) => {
            navigate(`/merchant/settings/taxes/${data[0]?.pkEcomStoreTax}`);
            triggerNotification('Tax created!', 'success');
          },
          onErrorCallback
        );
      }
    });
  };

  return !viewLoading ? (
    <PageView
      backPath={backPath}
      buttonAction={
        !!ecomStoreTax &&
        !duplicate && (
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <Button
              color="dark"
              component={Link}
              radius="md"
              size="xs"
              to={`/merchant/settings/taxes/${ecomStoreTax.pkEcomStoreTax}/duplicate`}
              type="button"
              variant="light"
            >
              Duplicate
            </Button>
          </AppFlexbox>
        )
      }
      title="Add store tax"
      width={900}
    >
      <AppStack
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSaveTax();
        }}
      >
        {duplicate ? (
          <PageCard padding={8}>
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="#eca70a" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Duplicate this tax by changing the <b>Title</b> and clicking
                save.
              </AppText>
            </AppFlexbox>
          </PageCard>
        ) : (
          <PageCard padding={8}>
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="dodgerblue" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Set a tax rate for your store and apply it to selected products
                or all items.
              </AppText>
            </AppFlexbox>
          </PageCard>
        )}

        <PageCard>
          <AppStack>
            <TextInput
              description="This title will be visible to customers and be displayed on their receipts."
              disabled={isSubmitting}
              label="Title"
              onChange={(e) =>
                setFormState({
                  ...formState,
                  name: e.currentTarget.value
                })
              }
              required
              value={formState.name}
            />

            <CustomNumberInput
              allowNegative={false}
              decimalScale={2}
              disabled={isSubmitting}
              label="Tax rate"
              onChange={(v) =>
                setFormState({
                  ...formState,
                  taxRate: v
                })
              }
              placeholder="13%"
              required
              rightSection={
                <AppText
                  style={{
                    fontWeight: 500,
                    color: '#666',
                    fontSize: 14
                  }}
                >
                  %
                </AppText>
              }
              value={formState.taxRate}
            />
          </AppStack>
        </PageCard>

        <PageCard style={{ padding: 0 }}>
          <AppStack style={{ gap: 0 }}>
            <AppFlexbox
              style={{
                padding: 16,
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                Products
              </AppText>

              <Button
                color="dark"
                disabled={isSubmitting}
                leftSection={<Plus />}
                onClick={() => onOpenModal(MENU_ACTIONS.ADD_PRODUCT)}
                size="compact-sm"
                type="button"
                variant="filled"
              >
                Add
              </Button>
            </AppFlexbox>
            <AppStack style={{ gap: 0 }}>
              {state.ecomStoreProducts.loading ? (
                <AppStack
                  style={{
                    gap: 5,
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px 10px',
                    flex: 1
                  }}
                >
                  <Loader color="dark" size={34} />
                </AppStack>
              ) : selectedProducts.length === 0 ? (
                <AppStack
                  style={{
                    padding: 16,
                    gap: 10,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AppText
                    style={{ fontWeight: 500, color: '#666', fontSize: 14 }}
                  >
                    No products have been added to this tax. It will be applied
                    to all products in your store.
                  </AppText>
                </AppStack>
              ) : (
                selectedProducts
                  .sort(
                    (a, b) =>
                      a.ecomVendorProduct.name.localeCompare(
                        b.ecomVendorProduct.name
                      ) || a.pkEcomStoreProduct - b.pkEcomStoreProduct
                  )
                  .map((p) => {
                    const productStatusInfo = getEcomStoreProductStatus(p);

                    const image =
                      p.ecomVendorProduct.previewImage ||
                      p.ecomVendorProduct.ecomVendorBundle?.ecomVendorBundleProducts
                        .sort((a, b) => a.sort - b.sort)
                        .map((bp) => bp.previewImage)
                        .filter(Boolean)[0];

                    return (
                      <React.Fragment key={p.pkEcomStoreProduct}>
                        <Divider />
                        <AppCard
                          radius={0}
                          shadow="none"
                          style={{ padding: 8 }}
                        >
                          <AppFlexbox
                            style={{
                              gap: 10,
                              flex: 1,
                              alignItems: 'center',
                              padding: '0px 8px'
                            }}
                          >
                            <AppFlexbox
                              style={{
                                flex: 1,
                                gap: 10,
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <AppFlexbox style={{ alignItems: 'center' }}>
                                <AppStack
                                  style={{
                                    width: 40,
                                    height: 40,
                                    border: !image
                                      ? 'solid 1px #dee2e6'
                                      : 'none',
                                    overflow: 'hidden',
                                    borderRadius: 4
                                  }}
                                >
                                  <Image fit="fill" h={40} src={image} w={40} />
                                </AppStack>
                                <AppStack style={{ fles: 1, gap: 0 }}>
                                  <Anchor
                                    onClick={() =>
                                      navigate(`/merchant/products/${p.uuid}`)
                                    }
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: '#000'
                                    }}
                                  >
                                    {p.ecomVendorProduct.name}
                                  </Anchor>
                                  <AppText
                                    style={{ fontSize: 12, color: '#666' }}
                                  >
                                    {p.ecomVendorProduct.vendorName}
                                  </AppText>
                                  <AppText
                                    style={{
                                      fontSize: 12,
                                      color: productStatusInfo.color
                                    }}
                                  >
                                    {productStatusInfo.label}
                                  </AppText>
                                </AppStack>
                                {p.ecomVendorProduct.ecomVendorBundle && (
                                  <Badge color="blue" size="xs">
                                    Bundle
                                  </Badge>
                                )}
                              </AppFlexbox>

                              <ActionableIcon
                                color="red"
                                disabled={isSubmitting}
                                onClick={() =>
                                  setFormState({
                                    ...formState,
                                    pkEcomStoreProducts: formState.pkEcomStoreProducts.filter(
                                      (pk) => pk !== p.pkEcomStoreProduct
                                    )
                                  })
                                }
                                radius="md"
                                variant="subtle"
                              >
                                <Trash />
                              </ActionableIcon>
                            </AppFlexbox>
                          </AppFlexbox>
                        </AppCard>
                      </React.Fragment>
                    );
                  })
              )}
            </AppStack>
          </AppStack>
        </PageCard>

        <AppFlexbox
          p={{ base: 16, sm: '16px 0px' }}
          style={{
            justifyContent: 'flex-end',
            flexDirection: isLargeMobileOrSmaller ? 'column-reverse' : 'row'
          }}
        >
          {!!ecomStoreTax && !duplicate && (
            <Button
              color="red"
              disabled={isSubmitting}
              onClick={() => {
                onOpenModal(VIEW_ACTIONS_ENUM.DELETE, ecomStoreTax);
              }}
              radius="md"
              size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
              type="button"
              variant="filled"
            >
              Delete tax
            </Button>
          )}
          <Button
            color="dark"
            disabled={!hasUnsavedChanges}
            id="save-button"
            loading={isSubmitting}
            radius="md"
            size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
            type="submit"
            variant="filled"
          >
            Save
          </Button>
        </AppFlexbox>
      </AppStack>

      {ConfirmDiscardModal}

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete tax"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText>
              Are you sure you want to delete <b>{modalState.item?.name}</b>?
              This action can’t be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteStoreTax(
            authState.pkEcomStore,
            [modalState.item.pkEcomStoreTax],
            () => {
              triggerNotification('Tax deleted!', 'success');
              navigate('/merchant/settings/taxes');
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${modalState.item?.name}?`}
      />

      <StoreProductAssignModal
        isOpen={
          modalState.isOpen && modalState.action === MENU_ACTIONS.ADD_PRODUCT
        }
        onClose={onCloseModal}
        onConfirm={(ecomStoreProducts) => {
          setFormState({
            ...formState,
            pkEcomStoreProducts: ecomStoreProducts.map(
              (p) => p.pkEcomStoreProduct
            )
          });
          onCloseModal();
        }}
        pkEcomStoreProducts={formState.pkEcomStoreProducts}
        title="Assign tax to products"
      />
    </PageView>
  ) : (
    <AppStack
      p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
      style={{
        flex: 1,
        margin: 'auto',
        width: '100%',
        maxWidth: 950,
        paddingTop: 16,
        gap: 16
      }}
    >
      <AppFlexbox
        p={{ base: '0px 8px', sm: 0 }}
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <AppFlexbox style={{ gap: 5 }}>
          <Skeleton height={30} width={200} />
        </AppFlexbox>
      </AppFlexbox>

      <AppStack style={{ gap: 16 }}>
        <PageCard>
          <AppStack style={{ gap: 10 }}>
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="50%" />
          </AppStack>
        </PageCard>
        <PageCard>
          <AppStack style={{ gap: 10 }}>
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="50%" />
          </AppStack>
        </PageCard>
        <PageCard>
          <AppStack style={{ gap: 10 }}>
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="50%" />
          </AppStack>
        </PageCard>
      </AppStack>
    </AppStack>
  );
};

StoreTaxEditView.propTypes = {
  duplicate: PropTypes.bool,
  ecomStore: PropTypes.object,
  loading: PropTypes.bool
};

export default StoreTaxEditView;
